/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Check from '@mui/icons-material/Check';
import PendingActions from '@mui/icons-material/PendingActions';
import Close from '@mui/icons-material/Close';
import Send from '@mui/icons-material/Send';
import NoAccounts from '@mui/icons-material/NoAccounts';
import Help from '@mui/icons-material/Help';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Rsvp from '@mui/icons-material/Rsvp';

import { Skeleton } from '@mui/material';
import ScheduleService from '../../../services/ScheduleService';
import { blueGrey } from '../../../utils/brandColors';
import { getScheduleResponsesForSchedule } from '../../../redux/schedule/actions';

const ParticipantListComponent = ({
  schedule,
  participants,
  occurrenceDate,
  defaultOpen,
  simplified,
}) => {
  const dispatch = useDispatch();

  const [responses, setResponses] = React.useState([]);
  const [openParticipantList, setOpenParticipantList] = React.useState(defaultOpen);
  const [open, setOpen] = React.useState(false);

  const {
    isFetchingScheduleResponses,
    scheduleResponses,
  } = useSelector((state) => state.schedule);

  useEffect(() => {
    setOpenParticipantList(defaultOpen);
  }, [defaultOpen]);

  useEffect(() => {
    if (schedule && schedule.publicId) {
      setResponses([]);
      dispatch(getScheduleResponsesForSchedule(schedule, occurrenceDate || null));
    }
  }, [schedule, occurrenceDate]);

  useEffect(() => {
    // map responses with user
    const updatedScheduleResponses = participants.map((u) => ({
      response: scheduleResponses
        // put responses for specific occurrence date first
        .sort((a, b) => new Date(a.occurrenceDate
          || 3000000000000).getTime() - new Date(b.occurrenceDate || 3000000000000).getTime())
        .find((r) => (r.responderId === u.auth0UserId || r.responderId === u.email)),
      user: u,
    }));

    setResponses(updatedScheduleResponses);
  }, [scheduleResponses])

  const handleRequestResponse = (participant) => {
    ScheduleService.sendScheduleInviteRequest(schedule.publicId, participant.auth0UserId || participant.email).then(() => {
      // success
      setOpen(true);
    });
  };

  if (!isFetchingScheduleResponses && participants.length === 0) {
    return (
      <Typography
        variant="caption"
        component="div"
        align="center"
        sx={{
          fontWeight: 300,
          mb: 1,
        }}
      >
        No participants...
      </Typography>
    )
  }

  const getIconForResponseStatus = (status, user) => {
    if (!simplified && (!status || status === 'None')) {
      return (
        <Box>
          <Tooltip title="Resend Invite"><Send
            sx={{
              color: blueGrey[400],
              mr: 0.5,
              '&:hover': {
                cursor: 'pointer',
                color: blueGrey[200],
              },
            }}
            onClick={() => handleRequestResponse(user)}
          />
          </Tooltip>
          <Tooltip title="No Response"><Help sx={{ color: blueGrey[100] }} /></Tooltip>
        </Box>
      );
    }
    if (status === 'Accepted') {
      return <Tooltip title="Accepted"><Check color="success" /></Tooltip>;
    }
    if (status === 'Declined') {
      return <Tooltip title="Declined"><Close color="error" /></Tooltip>;
    }
    if (status === 'Tentative') {
      return <Tooltip title="Tentative"><PendingActions color="warning" /></Tooltip>;
    }
    return <Tooltip title="No Response"><Help sx={{ color: blueGrey[100] }} /></Tooltip>;
  }

  const getIconsForResponse = (user, response) => (
    <Box>
      { schedule && ((user && user.id) || !!response || simplified) && (getIconForResponseStatus(response && response.status, user)) }
      {
        !simplified && !user.id && !response && (
          <Tooltip title="No Account - No invite responses can be recorded."><NoAccounts color="secondary" /></Tooltip>
        )
      }
    </Box>
  )

  const yesResponses = responses.filter((r) => r.response && r.response.status === 'Accepted').length;
  const noResponses = responses.filter((r) => r.response && r.response.status === 'Declined').length;
  const maybeResponses = responses.filter((r) => r.response && r.response.status === 'Tentative').length;
  const pendingResponses = responses.filter((r) => (r.response && r.response.status === 'None') || !r.response).length;

  return (
    <Box>
      <ListItemButton
        sx={{
          pt: 0,
          pb: 0,
        }}
        onClick={() => setOpenParticipantList(!openParticipantList)}
      >
        <ListItemIcon><Rsvp /></ListItemIcon>
        <ListItemText
          primary={`${yesResponses} Yes`}
          secondary={`${noResponses} No, ${maybeResponses} Maybe, ${pendingResponses} Pending`}
        />
        {(openParticipantList
          ? <ExpandLess sx={{ m: 1 }} onClick={(e) => { e.stopPropagation(); setOpenParticipantList(false); }} />
          : <ExpandMore sx={{ m: 1 }} onClick={(e) => { e.stopPropagation(); setOpenParticipantList(true); }} />)}
      </ListItemButton>
      <Collapse in={openParticipantList} timeout="auto" unmountOnExit>
        <List
          component="div"
          dense
          sx={{
            maxHeight: 400,
            overflow: 'auto',
          }}
        >
          {
            isFetchingScheduleResponses && [1, 2, 3].map((load) => (
              <ListItem
                key={`load-participant-${load}`}
                disablePadding
                component="div"
              >
                <ListItem>
                  <ListItemAvatar>
                    <Avatar />
                  </ListItemAvatar>
                  <Skeleton width={150} height={35} />
                </ListItem>
              </ListItem>
            ))
          }
          {
            !isFetchingScheduleResponses && (
              responses.sort((a, b) => -b.user.userFullName.localeCompare(a.user.userFullName)).map((r) => {
                const labelId = `participant-list-${r.user.auth0UserId || r.user.email || r.user.userFullName}-${(schedule && schedule.id) || '-1'}`;
                return (
                  <ListItem
                    key={labelId}
                    component="div"
                  >
                    <ListItemAvatar>
                      <Avatar
                        alt={r.user.userFullName}
                        src={r.user.avatar}
                        sx={{
                          width: 30,
                          height: 30,
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      id={labelId}
                      primary={r.user.userFullName}
                      secondary={`${(schedule && (r.user.email === schedule.createdByEmail || r.user.userFullName === schedule.createdByName) && '(organizer) ') || ''}${(r.response && r.response.note && `"${r.response.note}"`) || ''}`}
                    />
                    {
                      !(schedule && (r.user.email === schedule.createdByEmail || r.user.userFullName === schedule.createdByName))
                        && getIconsForResponse(r.user, r.response)
                    }
                  </ListItem>
                );
              })
            )
          }
        </List>
      </Collapse>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
      >
        <Alert onClose={() => setOpen(false)} severity="success" sx={{ width: '100%' }}>
          Invite successfully sent!
        </Alert>
      </Snackbar>
    </Box>
  );
};

ParticipantListComponent.propTypes = {
  schedule: PropTypes.object,
  participants: PropTypes.array.isRequired,
  occurrenceDate: PropTypes.any,
  defaultOpen: PropTypes.bool,
  simplified: PropTypes.bool,
};

ParticipantListComponent.defaultProps = {
  schedule: null,
  occurrenceDate: null,
  defaultOpen: false,
  simplified: false,
};

export default (ParticipantListComponent);

